import React from 'react';
import { sumItems, sumRemaining, remainingFor } from './productionUtils';
import {
  groupCustomerCount,
  DueDate,
  WoProgress,
  ShortBadge,
  BlockPacking,
  StockBalance,
  DefectCodeSelect,
  RejectQuantityInput,
  ReportQuantityStepper,
  ToReportHint
} from './WorkOrderRowParts';

// Work orders cards - tablet/mobile layout, swapped in via CSS at 900px and below
// (CARD_LAYOUT_QUERY)
const WorkOrdersCards = ({
  displayRows,
  selectedItems,
  quantities,
  rejectQuantities,
  defectCodes,
  defectCodeSelections,
  isShort,
  partBalances,
  stockFetchedAt,
  hideCustomerCol,
  hideDateCol,
  changedSerials,
  onSelectItem,
  onSelectGroup,
  onQuantityChange,
  onRejectQuantityChange,
  onDefectCodeChange
}) => (
  <div className="work-orders-cards">
    {displayRows.map((row) => {
      if (row.type === 'colour') {
        const colourSelected = row.wos.every(w => selectedItems.has(w.serialName));
        return (
          <div key={`card-colour-${row.key}`} className="wo-card-colour">
            <label className="wo-card-group-label">
              <input
                type="checkbox"
                checked={colourSelected}
                onChange={() => onSelectGroup(row.wos)}
                aria-label={`Select all ${row.wos.length} work orders in ${row.label}`}
              />
              <div className="colour-group-info">
                <span className="colour-group-label">{row.label}</span>
                <span className="colour-group-summary">
                  {sumRemaining(row.wos)} to make &middot; {row.wos.length} WOs
                </span>
              </div>
            </label>
          </div>
        );
      }
      if (row.type === 'group') {
        const groupSelected = row.wos.every(w => selectedItems.has(w.serialName));
        const first = row.wos[0];
        const groupCustomers = groupCustomerCount(row.wos);
        return (
          <div key={`card-group-${row.key}`} className="wo-card-group">
            <label className="wo-card-group-label">
              <input
                type="checkbox"
                checked={groupSelected}
                onChange={() => onSelectGroup(row.wos)}
                aria-label={`Select all ${row.wos.length} work orders for ${first.partName || first.partDescription}`}
              />
              <div className="sku-group-info">
                <span className="sku-group-desc">{first.partDescription || first.partName}</span>
                {first.partName && first.partDescription && (
                  <span className="sku-group-code">{first.partName}</span>
                )}
                <span className="sku-group-summary">
                  {row.wos.length} WOs
                  {groupCustomers > 1 && <> &middot; {groupCustomers} customers</>}
                  {' '}&middot; {sumItems(row.wos)} items
                </span>
              </div>
            </label>
          </div>
        );
      }
      const wo = row.wo;
      const reportedQty = wo.reportedQuantity || 0;
      const plannedQty = Number(wo.quantity) || 0;
      const remainingQty = remainingFor(wo);
      return (
        <div
          key={`card-${wo.serialName}`}
          className={`wo-card ${row.grouped ? 'grouped' : ''} ${selectedItems.has(wo.serialName) ? 'selected' : ''} ${changedSerials.has(wo.serialName) ? 'wo-changed' : ''}`}
        >
          <label className="wo-card-main">
            <input
              type="checkbox"
              checked={selectedItems.has(wo.serialName)}
              onChange={() => onSelectItem(wo.serialName)}
              aria-label={`Select ${wo.serialName}`}
            />
            <div className="wo-card-info">
              <span className="wo-card-part">{wo.partDescription || wo.partName}</span>
              {wo.partName && wo.partDescription && (
                <span className="wo-card-sku">{wo.partName}</span>
              )}
              <BlockPacking wo={wo} />
              <StockBalance wo={wo} balances={partBalances} fetchedAt={stockFetchedAt} />
              <div className="wo-card-meta">
                <span className="wo-card-serial">{wo.serialName}</span>
                {isShort(wo) && <ShortBadge />}
                {!hideCustomerCol && <span>{wo.customerName || wo.customerCode || '-'}</span>}
                {!hideCustomerCol && wo.salesOrder && <span className="sales-order">{wo.salesOrder}</span>}
                {!hideDateCol && <DueDate date={wo.endDate || wo.plannedDate} inline />}
              </div>
            </div>
          </label>
          {/* Progress on its own row, then Rejects and To Report side by side, then the
              reject reason across the full width */}
          <div className="wo-card-quantities">
            <div className="wo-card-qty wo-card-progress">
              <span className="wo-card-qty-label">Reported / Planned</span>
              <WoProgress
                reported={reportedQty}
                planned={plannedQty}
                remaining={remainingQty}
              />
            </div>
            <div className="wo-card-qty">
              <span className="wo-card-qty-label">Rejects</span>
              <RejectQuantityInput
                serialName={wo.serialName}
                value={rejectQuantities[wo.serialName]}
                onChange={onRejectQuantityChange}
              />
            </div>
            <div className="wo-card-qty">
              <span className="wo-card-qty-label">To Report</span>
              <ReportQuantityStepper
                wo={wo}
                value={quantities[wo.serialName]}
                onChange={onQuantityChange}
              />
              <ToReportHint wo={wo} value={quantities[wo.serialName]} onQuantityChange={onQuantityChange} />
            </div>
            <div className="wo-card-reason">
              <DefectCodeSelect
                serialName={wo.serialName}
                defectCodes={defectCodes}
                rejectQuantity={rejectQuantities[wo.serialName]}
                value={defectCodeSelections[wo.serialName]}
                onChange={onDefectCodeChange}
              />
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

export default WorkOrdersCards;
